import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, List, ListItemButton, ListItemText, Divider, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, CircularProgress, IconButton,
  Tooltip, Alert, Stack, Tab, Tabs,
} from '@mui/material';
import {
  Edit as EditIcon, RestartAlt as ResetIcon, Preview as PreviewIcon,
  Send as SendIcon, Save as SaveIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { emailTemplatesAPI } from '../services/apiService';
import { useAlert } from '../contexts/AlertContext';

const TEMPLATE_LABELS = {
  driver_invoice: 'Driver Invoice',
  password_reset: 'Password Reset',
  welcome_dispatcher: 'Welcome (Dispatcher)',
  welcome_driver: 'Welcome (Driver)',
  driver_application_invite: 'Driver Application Invite',
  application_received: 'Application Received',
  application_rejected: 'Application Rejected',
  username_changed: 'Username Changed',
};

const TEMPLATE_VARIABLES = {
  driver_invoice: ['CompanyName', 'DriverName', 'InvoiceNumber', 'AmountText', 'FromDate', 'ToDate'],
  password_reset: ['CompanyName', 'NewPassword'],
  welcome_dispatcher: ['CompanyName', 'RecipientName', 'Email', 'UserName', 'Password', 'DispatcherLoginUrl'],
  welcome_driver: ['CompanyName', 'RecipientName', 'Email', 'UserName', 'Password', 'AppStoreUrl', 'PlayStoreUrl'],
  driver_application_invite: ['CompanyName', 'ApplyLink', 'ExpiryDate'],
  application_received: ['CompanyName', 'DisplayName'],
  application_rejected: ['CompanyName', 'DisplayName', 'ReasonSection'],
  username_changed: ['CompanyName', 'RecipientName', 'NewUsername'],
};

function EditorToolbar({ editor, onInsertVariable, variables }) {
  if (!editor) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Button size="small" variant={editor.isActive('bold') ? 'contained' : 'outlined'} onClick={() => editor.chain().focus().toggleBold().run()}>B</Button>
      <Button size="small" variant={editor.isActive('italic') ? 'contained' : 'outlined'} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></Button>
      <Button size="small" variant={editor.isActive('bulletList') ? 'contained' : 'outlined'} onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</Button>
      <Button size="small" variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</Button>
      <Divider orientation="vertical" flexItem />
      <Typography variant="caption" sx={{ alignSelf: 'center', mr: 0.5 }}>Insert:</Typography>
      {variables.map(v => (
        <Chip
          key={v}
          label={`{{${v}}}`}
          size="small"
          onClick={() => onInsertVariable(v)}
          sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: 11 }}
        />
      ))}
    </Box>
  );
}

export default function EmailTemplateManager() {
  const { showAlert, showToast } = useAlert();
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [current, setCurrent] = useState(null); // { key, subject, bodyHtml, isCustomized, defaultSubject, defaultBodyHtml }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState('');
  const [previewDialog, setPreviewDialog] = useState(null); // { subject, bodyHtml }
  const [sendTestDialog, setSendTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [tabIndex, setTabIndex] = useState(0); // 0=visual, 1=html

  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false })],
    content: '',
    onUpdate: ({ editor }) => {
      if (current) setCurrent(prev => ({ ...prev, bodyHtml: editor.getHTML() }));
    },
    editable: editMode,
  });

  // Update editor editable state
  useEffect(() => {
    if (editor) editor.setEditable(editMode);
  }, [editMode, editor]);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await emailTemplatesAPI.getAll();
      setTemplates(data);
    } catch (e) {
      showToast('Failed to load email templates', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const selectTemplate = async (key) => {
    setSelectedKey(key);
    setEditMode(false);
    try {
      const data = await emailTemplatesAPI.getByKey(key);
      setCurrent(data);
      setSubjectDraft(data.subject);
      editor?.commands.setContent(data.bodyHtml);
    } catch {
      showToast('Failed to load template', 'error');
    }
  };

  const handleEdit = () => {
    if (!current) return;
    setSubjectDraft(current.subject);
    editor?.commands.setContent(current.bodyHtml);
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const body = tabIndex === 0 ? editor.getHTML() : current.bodyHtml;
      await emailTemplatesAPI.update(current.key, subjectDraft, body);
      showToast('Template saved', 'success');
      setEditMode(false);
      await selectTemplate(current.key);
      await loadTemplates();
    } catch {
      showToast('Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!current) return;
    showAlert('Reset Template', `Reset "${TEMPLATE_LABELS[current.key]}" to the built-in default? Any customizations will be lost.`, [
      { label: 'Cancel', color: 'inherit' },
      {
        label: 'Reset', color: 'error', onClick: async () => {
          try {
            await emailTemplatesAPI.reset(current.key);
            showToast('Template reset to default', 'success');
            await selectTemplate(current.key);
            await loadTemplates();
          } catch {
            showToast('Failed to reset template', 'error');
          }
        }
      }
    ]);
  };

  const handlePreview = async () => {
    try {
      const body = tabIndex === 0 ? editor.getHTML() : current.bodyHtml;
      const data = await emailTemplatesAPI.preview(current.key, subjectDraft || current.subject, body);
      setPreviewDialog(data);
    } catch {
      showToast('Failed to generate preview', 'error');
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    try {
      const body = tabIndex === 0 ? editor.getHTML() : current.bodyHtml;
      await emailTemplatesAPI.sendTest(current.key, testEmail, subjectDraft || current.subject, body);
      showToast(`Test email sent to ${testEmail}`, 'success');
      setSendTestDialog(false);
      setTestEmail('');
    } catch {
      showToast('Failed to send test email', 'error');
    }
  };

  const insertVariable = (varName) => {
    editor?.chain().focus().insertContent(`{{${varName}}}`).run();
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, height: '100%', minHeight: 500 }}>
      {/* Left: Template List */}
      <Paper sx={{ width: 240, flexShrink: 0, overflow: 'auto' }}>
        <Typography variant="subtitle2" sx={{ p: 2, fontWeight: 600 }}>Email Templates</Typography>
        <Divider />
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : (
          <List dense>
            {Object.keys(TEMPLATE_LABELS).map(key => {
              const tmpl = templates.find(t => t.key === key);
              return (
                <ListItemButton key={key} selected={selectedKey === key} onClick={() => selectTemplate(key)}>
                  <ListItemText
                    primary={TEMPLATE_LABELS[key]}
                    secondary={tmpl?.isCustomized ? <Chip label="Custom" size="small" color="warning" sx={{ height: 16, fontSize: 10 }} /> : null}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Right: Editor */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!current ? (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Select a template to edit</Typography>
          </Paper>
        ) : (
          <>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">{TEMPLATE_LABELS[current.key]}</Typography>
                <Stack direction="row" spacing={1}>
                  {!editMode ? (
                    <>
                      <Tooltip title="Edit"><IconButton onClick={handleEdit}><EditIcon /></IconButton></Tooltip>
                      <Tooltip title="Preview"><IconButton onClick={handlePreview}><PreviewIcon /></IconButton></Tooltip>
                      <Tooltip title="Send Test Email"><IconButton onClick={() => setSendTestDialog(true)}><SendIcon /></IconButton></Tooltip>
                      {current.isCustomized && <Tooltip title="Reset to Default"><IconButton color="warning" onClick={handleReset}><ResetIcon /></IconButton></Tooltip>}
                    </>
                  ) : (
                    <>
                      <Button variant="outlined" startIcon={<PreviewIcon />} onClick={handlePreview}>Preview</Button>
                      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                      <IconButton onClick={() => { setEditMode(false); selectTemplate(current.key); }}><CloseIcon /></IconButton>
                    </>
                  )}
                </Stack>
              </Box>

              {/* Subject */}
              <TextField
                label="Subject"
                fullWidth
                size="small"
                value={editMode ? subjectDraft : current.subject}
                onChange={e => setSubjectDraft(e.target.value)}
                InputProps={{ readOnly: !editMode }}
                sx={{ mb: 2 }}
              />

              {/* Available Variables */}
              <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Variables: </Typography>
                {(TEMPLATE_VARIABLES[current.key] || []).map(v => (
                  <Chip
                    key={v}
                    label={`{{${v}}}`}
                    size="small"
                    variant="outlined"
                    onClick={editMode ? () => insertVariable(v) : undefined}
                    sx={{ fontFamily: 'monospace', fontSize: 10, cursor: editMode ? 'pointer' : 'default' }}
                  />
                ))}
              </Box>

              {/* Editor Tabs */}
              <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 1 }}>
                <Tab label="Visual" />
                <Tab label="HTML" />
              </Tabs>

              {tabIndex === 0 && (
                <>
                  {editMode && <EditorToolbar editor={editor} onInsertVariable={insertVariable} variables={TEMPLATE_VARIABLES[current.key] || []} />}
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minHeight: 300, '& .ProseMirror': { outline: 'none', minHeight: 280 } }}>
                    <EditorContent editor={editor} />
                  </Box>
                </>
              )}

              {tabIndex === 1 && (
                <TextField
                  multiline
                  fullWidth
                  rows={14}
                  value={current.bodyHtml}
                  onChange={e => { setCurrent(prev => ({ ...prev, bodyHtml: e.target.value })); editor?.commands.setContent(e.target.value); }}
                  InputProps={{ readOnly: !editMode, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                />
              )}

              {!editMode && current.isCustomized && (
                <Alert severity="info" sx={{ mt: 1 }} action={
                  <Button size="small" color="inherit" onClick={() => {
                    setCurrent(prev => ({ ...prev, bodyHtml: prev.defaultBodyHtml, subject: prev.defaultSubject }));
                    editor?.commands.setContent(current.defaultBodyHtml);
                  }}>View Default</Button>
                }>This template has been customized.</Alert>
              )}
            </Paper>
          </>
        )}
      </Box>

      {/* Preview Dialog */}
      <Dialog open={!!previewDialog} onClose={() => setPreviewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Preview: {previewDialog?.subject}</DialogTitle>
        <DialogContent>
          <iframe
            title="email-preview"
            srcDoc={previewDialog?.bodyHtml}
            style={{ width: '100%', height: 500, border: 'none' }}
            sandbox="allow-same-origin"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Send Test Dialog */}
      <Dialog open={sendTestDialog} onClose={() => setSendTestDialog(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            label="Recipient Email"
            type="email"
            fullWidth
            sx={{ mt: 1 }}
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendTestDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendTest} disabled={!testEmail}>Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
